import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ErrorSharp as ErrorIcon } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import TaskPath from '~/modules/common/components/entities/task/TaskPath';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { useMeContext } from '~/modules/me/useMeContext';
import { RowLeftItemActions } from './RowLeftItemActions';
import { TaskAssignmentRole } from './TaskAssignmentRole';

const useTaskStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  taskName: {
    ...theme.typography.body2,
    color: theme.palette.text.primary
  }
}));

const useStyles = makeStyles(theme => ({
  root: {
    justifyContent: 'space-between',
    display: 'flex',
    width: '100%',
    alignItems: 'center'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(3),
    justifyContent: 'center'
  },
  codeLabel: {
    ...theme.typography.body2,
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  errorIcon: {
    fontSize: theme.typography.h5.fontSize,
    marginRight: theme.spacing(0.3),
    color: theme.palette.error.main
  }
}));

const generateRowHeaderId = ({ user, task }) =>
  `row-header-${user.id}-${task.id}`;

const RowLeftItem = ({
  user,
  projectId,
  taskResourceEstimate,
  hasTaskAllocation,
  handleRemoveTaskAssignment,
  isRowOverDistributed,
  classes: classesOverride
}) => {
  const taskClasses = useTaskStyles();
  const classes = useStyles({ classes: classesOverride });
  const {
    projectObjectPermissions: { canEditTasks, canViewCost }
  } = useProjectContext();
  const { formatMessage } = useIntl();
  const {
    featureFlags: { isRmp2025Q4Enhancements }
  } = useMeContext();

  const { fullPath, task, projectRole, isUnassigned } = taskResourceEstimate;
  const { code } = task;
  const taskPath = useMemo(
    () => ({
      path: fullPath.map(p => p.displayText)
    }),
    [fullPath]
  );
  const showActions = canEditTasks && !isUnassigned;

  return (
    <>
      <div className={classes.root}>
        <div
          className={classes.container}
          role="rowheader"
          id={generateRowHeaderId({ user, task })}
          tabIndex={0}
        >
          <TaskPath task={taskPath} classes={taskClasses}>
            <Typography component="span" className={classes.codeLabel}>
              {code}
            </Typography>
          </TaskPath>
          <TaskAssignmentRole
            projectRole={projectRole}
            isRmp2025Q4Enhancements={isRmp2025Q4Enhancements}
          />
        </div>
        {isRowOverDistributed && (
          <Tooltip
            title={formatMessage({ id: 'taskAssignments.rowOverAllocation' })}
            placement="right"
          >
            <ErrorIcon className={classes.errorIcon} />
          </Tooltip>
        )}
      </div>
      {showActions && (
        <RowLeftItemActions
          task={task}
          user={user}
          projectId={projectId}
          hasTaskAllocation={hasTaskAllocation}
          handleRemoveTaskAssignment={handleRemoveTaskAssignment}
          canViewCost={canViewCost}
        />
      )}
    </>
  );
};

RowLeftItem.propTypes = {
  taskResourceEstimate: PropTypes.object,
  user: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,
  hasTaskAllocation: PropTypes.bool.isRequired,
  handleRemoveTaskAssignment: PropTypes.func,
  isRowOverDistributed: PropTypes.bool,
  classes: PropTypes.object
};

export default RowLeftItem;
